<template>
  <div class="lab-works-container">
    <div v-if="loading" class="text-center py-8">
      <p class="text-gray-500">Загрузка лабораторных работ...</p>
    </div>

    <div v-else-if="!canEdit && isBlocked" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="font-semibold text-red-700">Доступ к лабораторным работам заблокирован.</p>
      <p class="text-sm text-red-600 mt-1">Обратитесь к модератору для сброса попыток и уточнения условий повторного прохождения.</p>
    </div>

    <div v-else-if="!canEdit && noActiveApplication" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="font-semibold text-red-700">Доступ к лабораторным работам закрыт.</p>
      <p class="text-sm text-red-600 mt-1">Нет активной одобренной заявки на курс. Подайте заявку повторно.</p>
    </div>

    <div v-else-if="labWorks.length === 0" class="text-center py-8">
      <p class="text-gray-500">Нет лабораторных работ в этой теме</p>
    </div>

    <div v-else class="space-y-4">
      <!-- Admin: Create new lab work button -->
      <div v-if="canEdit" class="mb-6">
        <button
          @click="openCreateModal"
          class="bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-600 transition"
        >
          + Создать лабораторную работу
        </button>
      </div>

      <!-- Lab works list -->
      <div class="grid gap-4">
        <div
          v-for="labWork in labWorks"
          :key="labWork.uuid"
          class="bg-white p-6 rounded-lg shadow hover:shadow-lg transition"
        >
          <!-- Lab work header -->
          <div class="flex justify-between items-start mb-4">
            <div class="flex-1">
              <h3 class="text-xl font-bold text-gray-800">{{ labWork.title }}</h3>
              <p class="text-sm text-gray-500 mt-1">
                {{ labWork.question_amount }} {{ formatQuestions(labWork.question_amount) }}
              </p>
            </div>
            <div v-if="canEdit" class="flex gap-2">
              <button
                @click="editLabWork()"
                class="bg-yellow-500 text-white px-3 py-2 rounded text-sm hover:bg-yellow-600 transition"
              >
                Редактировать
              </button>
              <button
                @click="deleteLabWork()"
                class="bg-red-500 text-white px-3 py-2 rounded text-sm hover:bg-red-600 transition"
              >
                Удалить
              </button>
            </div>
          </div>

          <!-- Lab work image -->
          <img
            v-if="labWork.image"
            :src="labWork.image"
            :alt="labWork.title"
            class="w-full max-h-80 object-contain rounded-lg mb-4 bg-gray-50"
          />

          <!-- Lab work description -->
          <p v-if="labWork.content" class="text-gray-700 mb-4">{{ labWork.content }}</p>

          <!-- Lab work status for student -->
          <div v-if="!canEdit" class="mb-4 flex items-center gap-2">
            <span v-if="labWork.result !== null" class="text-sm">
              Ваш результат:
              <strong :style="{ color: labWork.result >= 50 ? '#10b981' : '#ef4444' }">
                {{ labWork.result.toFixed(1) }}%
              </strong>
            </span>
            <span v-else class="text-sm text-blue-600">Не пройдена</span>
          </div>

          <!-- Start/Edit button -->
          <button
            v-if="!canEdit && !labWork.result"
            @click="goToLabWork(labWork.uuid)"
            class="w-full bg-green-500 text-white font-bold py-2 px-4 rounded hover:bg-green-600 transition"
          >
            Начать лабораторную работу
          </button>
          <button
            v-else-if="!canEdit && labWork.result"
            @click="goToLabWork(labWork.uuid)"
            class="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-600 transition"
          >
            Просмотреть результаты
          </button>
          <button
            v-else
            @click="goToLabWorkEdit(labWork.uuid)"
            class="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-600 transition"
          >
            Редактировать вопросы
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { HEADERS_TOKEN_JSON, API_CLIENT } from '@/const/uri';
import { mapActions } from 'vuex';

export default {
  props: {
    themeUUID: {
      type: String,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      labWorks: [],
      loading: true,
      isBlocked: false,
      noActiveApplication: false,
    };
  },
  mounted() {
    this.loadLabWorks();
  },
  methods: {
    ...mapActions(['toggleAlert']),

    async loadLabWorks() {
      try {
        this.loading = true;
        const response = await axios({
          method: 'GET',
          url: `${API_CLIENT.labWorks}?tsor=${this.themeUUID}`,
          headers: HEADERS_TOKEN_JSON(),
        });
        this.labWorks = response.data.data.lab_works || [];
        this.isBlocked = response.data.data.is_blocked || false;
        this.noActiveApplication = response.data.data.no_active_application || false;
      } catch (error) {
        console.error('Ошибка при загрузке лаб-работ:', error);
        this.toggleAlert({
          message: 'Ошибка при загрузке лаб-работ',
          isError: true,
        });
      } finally {
        this.loading = false;
      }
    },

    formatQuestions(count) {
      const mod = count % 10;
      const mod100 = count % 100;
      if (mod100 >= 11 && mod100 <= 19) return 'задач';
      if (mod === 1) return 'задача';
      if (mod >= 2 && mod <= 4) return 'задачи';
      return 'задач';
    },

    goToLabWork(labWorkUUID) {
      this.$router.push({
        name: 'ClientLabWork',
        params: { labWorkUUID },
      });
    },

    goToLabWorkEdit() {
      // TODO: Implement admin edit route
      this.toggleAlert({
        message: 'Редактирование лаб-работы в разработке',
        isError: false,
      });
    },

    editLabWork() {
      this.goToLabWorkEdit();
    },

    async deleteLabWork() {
      if (!confirm('Вы уверены, что хотите удалить эту лабораторную работу?')) {
        return;
      }
      try {
        // TODO: Implement delete API call
        this.toggleAlert({
          message: 'Удаление лаб-работы в разработке',
          isError: false,
        });
      } catch (error) {
        console.error('Ошибка при удалении лаб-работы:', error);
        this.toggleAlert({
          message: 'Ошибка при удалении лаб-работы',
          isError: true,
        });
      }
    },

    openCreateModal() {
      this.toggleAlert({
        message: 'Создание новой лаб-работы в разработке',
        isError: false,
      });
    },
  },
};
</script>

<style scoped>
.lab-works-container {
  padding: 20px 0;
}
</style>
